import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import useLanguage from 'flamingo/hooks/useLanguage'
import { YYYY_MM_DD } from 'mynt-components/Constants'
import FigmaBox from 'mynt-components/components/FigmaBox'
import FigmaButton from 'mynt-components/components/FigmaButton'
import ModalExperimental from 'mynt-components/components/ModalExperimental'
import type React from 'react'
import { useForm } from 'react-hook-form'
import { getText, TextKeys } from 'tiger/libs/TextRepository'
import * as Yup from 'yup'
import { useCreateAccountingYear, useUpdateAccountingYear } from '../api/react-query'
import type { AccountXYear } from '../api/swagger/definitions/backoffice'
import { DatePickerControllerV2, SwitchController, TextFieldController } from '../components/react-hook-components'
import TextContainer from '../components/TextContainer'
import Spacings from '../figma/tokens/Spacings'
import { captureException } from '../helpers/sentryHelpers'
import ModalExperimentalFooter from '../mynt-components/components/ModalExperimentalFooter'
import ModalExperimentalHeader from '../mynt-components/components/ModalExperimentalHeader'

export enum YearFormTypes {
  CREATE = 'create',
  EDIT = 'edit',
}

interface YearFormProps {
  isOpen: boolean
  type: YearFormTypes
  values?: AccountXYear
  onModalCancel: () => void
}

const AccountingYearModal: React.FC<YearFormProps> = ({ type, values, onModalCancel }) => {
  const createAccountingYearMutation = useCreateAccountingYear()
  const updateAccountingYearMutation = useUpdateAccountingYear(values?.id as string)
  const language = useLanguage()

  const initialValues: AccountXYear = {
    name: values?.name || '',
    fromDate: values?.fromDate || dayjs().format(YYYY_MM_DD),
    toDate: values?.toDate || dayjs().format(YYYY_MM_DD),
    default: values?.default || false,
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().typeError(getText(TextKeys.createYearNameError, language)).required(getText(TextKeys.createYearNameError, language)),
    fromDate: Yup.string()
      .typeError(getText(TextKeys.createYearFromDateError, language))
      .required(getText(TextKeys.createYearFromDateError, language)),
    toDate: Yup.string()
      .typeError(getText(TextKeys.createYearToDateError, language))
      .required(getText(TextKeys.createYearToDateError, language)),
    default: Yup.boolean()
      .typeError(getText(TextKeys.mFRegisterPaymentDecriptionError, language))
      .required(getText(TextKeys.mFRegisterPaymentDecriptionError, language)),
  })

  const { control, handleSubmit } = useForm<AccountXYear>({ defaultValues: initialValues, resolver: yupResolver(validationSchema) })

  const onSubmit = async (values: AccountXYear) => {
    if (type === YearFormTypes.EDIT) {
      return updateAccountingYearMutation
        .mutateAsync(values)
        .then((res) => {
          onModalCancel()
          return res
        })
        .catch((err) => captureException(err))
    }

    if (type === YearFormTypes.CREATE) {
      return createAccountingYearMutation
        .mutateAsync(values)
        .then((res) => {
          onModalCancel()
          return res
        })
        .catch((err) => captureException(err))
    }
  }
  return (
    <ModalExperimental
      onClose={onModalCancel}
      backOfficeStyling
      heading={() => (
        <ModalExperimentalHeader>
          <TextContainer textKey={type === YearFormTypes.EDIT ? TextKeys.editYearHeading : TextKeys.createYearHeading} />
        </ModalExperimentalHeader>
      )}
    >
      <form name="modal_years" onSubmit={handleSubmit(onSubmit)}>
        <FigmaBox
          fullWidth
          direction="column"
          justify="space-between"
          align="flex-start"
          gap={Spacings.large}
          top={Spacings.large}
          bottom={Spacings.large}
          left={Spacings.medium}
          right={Spacings.medium}
        >
          <FigmaBox fullWidth direction="row" align="flex-start" justify="space-between" gap={Spacings.medium}>
            <FigmaBox fullWidth>
              <DatePickerControllerV2 control={control} name="fromDate" labelTextKey={TextKeys.createYearFromDateLabel} />
            </FigmaBox>
            <FigmaBox fullWidth>
              <DatePickerControllerV2 control={control} name="toDate" labelTextKey={TextKeys.createYearToDateLabel} />
            </FigmaBox>
          </FigmaBox>

          <FigmaBox fullWidth direction="row" align="flex-start" justify="space-between" gap={Spacings.medium}>
            <FigmaBox flex="0.5">
              <TextFieldController control={control} name="name" labelTextKey={TextKeys.createYearNameLabel} />
            </FigmaBox>
            <FigmaBox />
          </FigmaBox>

          <FigmaBox fullWidth direction="row" align="flex-start" justify="space-between" gap={Spacings.medium}>
            <FigmaBox flex="0.5" justify="space-between" direction="row">
              <FigmaBox direction="column">
                <TextContainer textKey={TextKeys.createYearNameDeafaultHeading} />
                <TextContainer textKey={TextKeys.createYearNameDeafaultBody} />
              </FigmaBox>
              <SwitchController control={control} name="default" />
            </FigmaBox>
            <FigmaBox />
          </FigmaBox>
        </FigmaBox>

        <ModalExperimentalFooter>
          <FigmaBox direction="row" fullWidth align="flex-start" justify="flex-end" gap={Spacings.tiny}>
            <FigmaButton fullWidth={false} variant="tertiary" textKey={TextKeys.createYearNameButtonCancel} onClick={onModalCancel} />
            <FigmaButton fullWidth={false} textKey={TextKeys.editYearButtonSave} />
          </FigmaBox>
        </ModalExperimentalFooter>
      </form>
    </ModalExperimental>
  )
}

// Mounted only while open, so each opening starts from fresh initial values
export const _ModalAccountingYear: React.FC<YearFormProps> = (props) => (props.isOpen ? <AccountingYearModal {...props} /> : null)
